import { Link } from "react-router-dom";
import { ArrowUpRight, Layers, Palette, Ship } from "lucide-react";
import { LazyImage } from "@/components/site/LazyImage";
import { Reveal, MaskReveal } from "@/components/site/Reveal";
import { Ribbon } from "@/components/site/Ribbon";
import { IMAGES } from "@/config/images.config";
import { PRODUCT_CATEGORIES } from "@/config/site";

const YARNS = ["100% Cotton", "80/20 C/PC Blend", "Recycled", "Zero-Twist", "Combed", "Open-End", "Ring-Spun", "Bamboo Blend"];
const CUSTOM = ["Size", "GSM", "Yarn Quality", "Composition", "Yarn Count", "Colour", "Design", "Construction", "Border", "Dobby", "Jacquard", "Embroidery", "Logo", "Packaging"];

export default function Products() {
  return (
    <div data-testid="products-page">
      {/* HEADER */}
      <section className="relative overflow-hidden bg-navy px-6 pb-24 pt-40 text-white md:px-12 md:pb-28 md:pt-52 lg:px-16" data-testid="products-hero">
        <div className="absolute inset-0 opacity-20">
          <img src={IMAGES.products.hero} alt="Terry towels" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/40" />
        <div className="relative mx-auto max-w-[1600px]">
          <Reveal><p className="mb-6 font-display text-xs font-semibold uppercase tracking-[0.34em] text-sky md:text-sm">Manufacturing & Supply</p></Reveal>
          <h1 className="font-display font-black leading-[0.9] tracking-tighter text-[clamp(2.5rem,12vw,7rem)]">
            <MaskReveal lines={["Terry Towel", "Products"]} />
          </h1>
          <Reveal delay={0.4}>
            <p className="mt-8 font-display text-base font-light uppercase tracking-[0.2em] text-white/70 md:text-xl">
              Quality • Comfort • Absorbency • Customized Manufacturing
            </p>
            <p className="mt-8 max-w-3xl text-sm leading-relaxed text-white/60 md:text-base">
              We manufacture and supply a wide range of customized Terry Towel products for domestic, hospitality, institutional and export markets. Our towels are available in different sizes, GSM, colours, designs, constructions and yarn qualities — manufactured according to buyer specifications.
            </p>
          </Reveal>
        </div>
      </section>

      <Ribbon />

      {/* PRODUCT GRID */}
      <section className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32 lg:px-16" data-testid="products-grid">
        <Reveal className="mb-14">
          <p className="font-display text-xs font-bold uppercase tracking-[0.24em] text-sky">Our Products</p>
          <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-navy md:text-6xl">Our full range</h2>
        </Reveal>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PRODUCT_CATEGORIES.map((p, i) => (
            <Reveal key={p.key} delay={(i % 3) * 0.08}>
              <Link
                to={`/products/${p.key}`}
                data-testid={`product-card-${p.key}`}
                className="group block h-full w-full overflow-hidden rounded-3xl border border-black/10 bg-white text-left transition-all duration-500 hover:border-sky/60 hover:shadow-xl hover:shadow-navy/5"
              >
                <div className="relative overflow-hidden rounded-t-3xl">
                  <LazyImage
                    src={IMAGES.categories[p.key].hero}
                    alt={p.name}
                    className="aspect-[4/3]"
                    imgClassName="group-hover:scale-105 transition-all duration-700"
                  />
                  <span className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-navy opacity-0 transition-all duration-500 group-hover:opacity-100">
                    <ArrowUpRight className="h-5 w-5" />
                  </span>
                </div>
                <div className="p-7">
                  <h3 className="font-display text-2xl font-bold tracking-tight text-navy">{p.name}</h3>
                  <p className="mt-2 font-display text-xs font-semibold uppercase tracking-[0.14em] text-sky">{p.spec}</p>
                  <p className="mt-4 text-sm leading-relaxed text-navy/55">{p.blurb}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-navy transition-colors group-hover:text-sky">
                    View details <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* YARN & QUALITY */}
      <section className="bg-navy px-6 py-24 text-white md:px-12 md:py-32 lg:px-16" data-testid="yarn-section">
        <div className="mx-auto grid max-w-[1600px] gap-14 lg:grid-cols-[1fr_1.1fr] lg:gap-24">
          <Reveal>
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 text-sky"><Layers className="h-6 w-6" /></span>
            <h2 className="mt-8 font-display text-4xl font-extrabold tracking-tight md:text-6xl">Yarn & Quality Options</h2>
            <p className="mt-6 max-w-md text-white/60">Every towel is built on the right yarn. Choose from a full spectrum of qualities to match performance, hand-feel and price point.</p>
            <div className="mt-10 overflow-hidden rounded-3xl">
              <LazyImage src={IMAGES.products.yarn} alt="Cotton yarn" className="aspect-[16/9]" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="flex flex-wrap content-center gap-3">
            {YARNS.map((y) => (
              <span key={y} data-testid={`yarn-${y}`} className="rounded-full border border-white/15 bg-white/[0.04] px-6 py-3 font-display text-sm font-semibold tracking-tight transition-colors duration-300 hover:border-sky hover:bg-sky hover:text-navy">
                {y}
              </span>
            ))}
          </Reveal>
        </div>
      </section>

      {/* CUSTOMIZED MANUFACTURING */}
      <section className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32 lg:px-16" data-testid="custom-section">
        <Reveal className="mb-14 max-w-2xl">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-navy/15 text-sky"><Palette className="h-6 w-6" /></span>
          <h2 className="mt-8 font-display text-4xl font-extrabold tracking-tight text-navy md:text-6xl">Customized Manufacturing</h2>
          <p className="mt-6 text-navy/60">Build your towel exactly to spec. We manufacture to your requirements across every variable that matters.</p>
        </Reveal>
        <div className="flex flex-wrap gap-3">
          {CUSTOM.map((c) => (
            <span key={c} data-testid={`custom-${c}`} className="rounded-full border border-black/10 bg-white px-6 py-3 font-display text-sm font-semibold tracking-tight text-navy transition-colors duration-300 hover:bg-navy hover:text-white">
              {c}
            </span>
          ))}
        </div>
      </section>

      {/* EXPORT & BULK */}
      <section className="relative overflow-hidden bg-navy px-6 py-24 text-white md:px-12 md:py-32 lg:px-16" data-testid="export-section">
        <div className="absolute inset-0 opacity-15">
          <img src={IMAGES.products.export} alt="Export containers" className="h-full w-full object-cover" />
        </div>
        <div className="relative mx-auto grid max-w-[1600px] gap-12 lg:grid-cols-[1.3fr_1fr] lg:items-end">
          <Reveal>
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 text-sky"><Ship className="h-6 w-6" /></span>
            <h2 className="mt-8 font-display text-4xl font-black leading-[0.95] tracking-tighter md:text-7xl">Export & Bulk Orders</h2>
            <p className="mt-6 max-w-xl text-white/70">
              We cater to international buyers, hospitality businesses, distributors, institutions and bulk requirements — with products developed according to specific quality, performance and market requirements. Custom sizes, GSM, yarn qualities and specifications are available as per buyer requirements.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/contact" data-testid="export-cta" className="group inline-flex items-center gap-2 rounded-full bg-sky px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white hover:text-navy">
              Request a Bulk Quote
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
